import { useCallback, useContext, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import useShortcuts from '../../hooks/useShortcuts'
import useSelectView from '../../hooks/useSelectView'
import { selectActiveChartId } from '../../selectors/chart'
import { ChartViewKey } from '../../assets/constants'
import { AppContext } from '../../App'
import { ChartShortcutsKeyName } from './ShortcutsKeymap'
import { ShortcutsCategories, ShortcutType } from './Shortcuts.types'
import { toggleAppFullScreen } from './toggleAppFullScreen'
import ChartKeyboardShortcutsHelp from './ChartKeyboardShortcutsHelp'

/**
 * Registers keyboard shortcuts for chart navigation and control
 */
const ChartShortcutsRegister = () => {
    const dispatch = useDispatch()
    const view = useSelectView()
    const activeChartId = useSelector(selectActiveChartId)
    const appContext = useContext(AppContext)
    const [showShortcutsHelp, setShowShortcutsHelp] = useState(false)

    const toggleFullScreen = useCallback(() => {
        toggleAppFullScreen(dispatch, appContext, view, 'keyboard')
    }, [appContext, dispatch, view])

    const handleCloseShortcutsHelp = useCallback(() => {
        setShowShortcutsHelp(false)
    }, [])

    const shortcuts: ShortcutType = useMemo(() => {
        const chart = appContext.getChart(activeChartId)
        const map: ShortcutType = {
            [ChartShortcutsKeyName.showHideFullScreen]: toggleFullScreen,
        }
        if (chart?.resetTimeScale) {
            map[ChartShortcutsKeyName.resetChartView] = () => {
                AnalyticsManager.logEvent('Reset Chart View', { source: 'keyboard' })
                chart.resetTimeScale()
            }
        }
        map[ChartShortcutsKeyName.scrollToRecentBar] = () => {
            const activeChart = appContext.getChart(activeChartId)
            if (activeChart?.gotoCurrentTime) {
                activeChart.gotoCurrentTime()
                return
            }
            appContext.getMultiStrikeChart(ChartViewKey.MULTI_STRIKES_CHART)?.gotoCurrentTime()
        }

        // Zoom shortcuts
        if (chart?.zoomIn) {
            map[ChartShortcutsKeyName.zoomIn] = (event: KeyboardEvent) => {
                event.preventDefault()
                AnalyticsManager.logEvent('Chart Zoom In', { source: 'keyboard' })
                chart.zoomIn()
            }
        }
        if (chart?.zoomOut) {
            map[ChartShortcutsKeyName.zoomOut] = (event: KeyboardEvent) => {
                event.preventDefault()
                AnalyticsManager.logEvent('Chart Zoom Out', { source: 'keyboard' })
                chart.zoomOut()
            }
        }

        // Pan shortcuts
        if (chart?.panLeft) {
            map[ChartShortcutsKeyName.panLeft] = (event: KeyboardEvent) => {
                event.preventDefault()
                chart.panLeft(false)
            }
            map[ChartShortcutsKeyName.panLeftFast] = (event: KeyboardEvent) => {
                event.preventDefault()
                chart.panLeft(true)
            }
        }
        if (chart?.panRight) {
            map[ChartShortcutsKeyName.panRight] = (event: KeyboardEvent) => {
                event.preventDefault()
                chart.panRight(false)
            }
            map[ChartShortcutsKeyName.panRightFast] = (event: KeyboardEvent) => {
                event.preventDefault()
                chart.panRight(true)
            }
        }

        // Keyboard shortcuts help
        map[ChartShortcutsKeyName.openKeyboardShortcutsHelp] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Open Keyboard Shortcuts Help', { source: 'keyboard' })
            setShowShortcutsHelp(true)
        }

        // Move to first/last bar
        if (chart?.moveToFirstBar) {
            map[ChartShortcutsKeyName.moveToFirstBar] = (event: KeyboardEvent) => {
                event.preventDefault()
                AnalyticsManager.logEvent('Move to First Bar', { source: 'keyboard' })
                chart.moveToFirstBar()
            }
        }

        if (chart?.moveToLastBar) {
            map[ChartShortcutsKeyName.moveToLastBar] = (event: KeyboardEvent) => {
                event.preventDefault()
                AnalyticsManager.logEvent('Move to Last Bar', { source: 'keyboard' })
                chart.moveToLastBar()
            }
        }

        if (chart?.moveToRealtimeBar) {
            map[ChartShortcutsKeyName.moveToRealtimeBar] = (event: KeyboardEvent) => {
                event.preventDefault()
                AnalyticsManager.logEvent('Move to Realtime Bar', { source: 'keyboard' })
                chart.moveToRealtimeBar()
            }
        }

        // Undo/Redo - Connected to the history manager
        if (chart?.historyManager) {
            map[ChartShortcutsKeyName.undo] = (event: KeyboardEvent) => {
                event.preventDefault()
                AnalyticsManager.logEvent('Undo', { source: 'keyboard' })
                chart.historyManager.undo()
            }

            map[ChartShortcutsKeyName.redo] = (event: KeyboardEvent) => {
                event.preventDefault()
                AnalyticsManager.logEvent('Redo', { source: 'keyboard' })
                chart.historyManager.redo()
            }
        }

        // Text notes and other drawing tools
        map[ChartShortcutsKeyName.addTextNote] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Add Text Note', { source: 'keyboard' })

        }

        // Save chart image
        map[ChartShortcutsKeyName.saveChartImage] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Save Chart Image', { source: 'keyboard' })

            if (chart?.getChartApi) {
                try {
                    const api = chart.getChartApi()
                } catch (error) {
                    console.error('Failed to save chart image:', error)
                }
            }
        }

        // Go to date - This should open the date picker
        map[ChartShortcutsKeyName.goToDate] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Go to Date', { source: 'keyboard' })
            // This needs to trigger the date picker dialog
            // The chart component would need to expose this functionality
            console.log('Go to date - requires date picker dialog integration')
        }

        // Replay controls - Connected to replay manager
        const replayManager = appContext.replayManager
        if (replayManager) {
            map[ChartShortcutsKeyName.replayPlayPause] = (event: KeyboardEvent) => {
                event.preventDefault()
                AnalyticsManager.logEvent('Replay Play/Pause', { source: 'keyboard' })
                // Toggle between play and stop
                if (replayManager.playTimeoutId) {
                    replayManager.stop()
                } else {
                    replayManager.play()
                }
            }

            map[ChartShortcutsKeyName.replayStepForward] = (event: KeyboardEvent) => {
                event.preventDefault()
                AnalyticsManager.logEvent('Replay Step Forward', { source: 'keyboard' })
                replayManager.gotoNextCandle()
            }
        }

        // Toggle maximize chart (fullscreen)
        map[ChartShortcutsKeyName.toggleMaximizeChart] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Toggle Maximize Chart', { source: 'keyboard' })
            toggleFullScreen()
        }

        // Switch between charts in multi-chart layout
        // Note: Tab and Shift+Tab are browser defaults for focus management
        // We let them work naturally for accessibility
        map[ChartShortcutsKeyName.switchNextChart] = () => {
            AnalyticsManager.logEvent('Switch Next Chart', { source: 'keyboard' })
            // Browser's natural Tab behavior handles focus navigation
        }

        map[ChartShortcutsKeyName.switchPrevChart] = () => {
            AnalyticsManager.logEvent('Switch Previous Chart', { source: 'keyboard' })
            // Browser's natural Shift+Tab behavior handles focus navigation
        }

        return map
    }, [activeChartId, appContext, toggleFullScreen])

    useShortcuts(ShortcutsCategories.CHART, shortcuts)

    return <ChartKeyboardShortcutsHelp open={showShortcutsHelp} onClose={handleCloseShortcutsHelp} />
}

export default ChartShortcutsRegister
